import { useId, type CSSProperties } from 'react'

/**
 * Die Marke von Raporto.
 *
 * Die Startseite, die App-Vorschau und die übrigen öffentlichen Seiten sollen
 * nicht verschiedene Zeichen erzählen. Deshalb lebt das neue farbige Raporto-
 * Zeichen hier zentral und nicht mehr als Sonderfall in der Landingpage.
 */

export type MarkenVariante = 'farbe' | 'einfarbig' | 'hell'

const ZEICHEN_FELD = 64
const KACHEL_FELD = 128
const ZEICHEN_PFAD =
  'M10 9h24c13 0 22 8 22 20 0 8-4 14-11 17l11 9H40L20 37h14c6 0 10-3 10-8s-4-8-10-8H10V9Z'
const ZEICHEN_BEIN = 'M10 33h16l20 22H28L10 36v-3Z'

type LogoStil = CSSProperties & {
  '--rp-logo-groesse': string
  '--rp-logo-wortgroesse': string
  '--rp-logo-claimgroesse': string
}

function bereinigteKennung(kennung: string): string {
  return kennung.replace(/[^a-zA-Z0-9_-]/g, '')
}

function MarkenVerlauf({ kennung }: { kennung: string }): JSX.Element {
  return (
    <linearGradient id={kennung} x1="4" y1="2" x2="60" y2="62" gradientUnits="userSpaceOnUse">
      <stop offset="0%" stopColor="#65e2ff" />
      <stop offset="44%" stopColor="#9a7cff" />
      <stop offset="76%" stopColor="#ff82e3" />
      <stop offset="100%" stopColor="#ffc4a7" />
    </linearGradient>
  )
}

export function Marke({
  groesse = 32,
  mitText = true,
  mitClaim = false,
  variante = 'farbe',
  unterzeile
}: {
  /** Höhe des Zeichens in Pixeln. Wort und Unterzeile folgen proportional. */
  groesse?: number
  mitText?: boolean
  /** Die Unterzeile gehört nur dorthin, wo die Marke genug Raum hat. */
  mitClaim?: boolean
  variante?: MarkenVariante
  /** Ein Zusatz neben der Marke – etwa ein Status – bleibt ausserhalb des Logos. */
  unterzeile?: string
}): JSX.Element {
  const reactKennung = useId()
  const kennung = `rp-marke-${variante}-${groesse}-${bereinigteKennung(reactKennung)}`
  const istFarbig = variante === 'farbe'
  const istHell = variante === 'hell'
  const logoStil = {
    '--rp-logo-groesse': `${groesse}px`,
    '--rp-logo-wortgroesse': `${Math.max(13, groesse * (mitClaim ? 0.72 : 0.78))}px`,
    '--rp-logo-claimgroesse': `${Math.max(8, groesse * 0.285)}px`,
    color: istHell ? '#ffffff' : undefined
  } as LogoStil
  const zeichenFuellung = istFarbig ? `url(#${kennung})` : 'currentColor'
  const beinFuellung = istFarbig ? '#ffc2d4' : 'currentColor'

  return (
    <span className={`rp-logo rp-logo-${variante}`} style={logoStil}>
      <svg
        className="rp-logo-marke"
        viewBox={`0 0 ${ZEICHEN_FELD} ${ZEICHEN_FELD}`}
        width={groesse}
        height={groesse}
        aria-hidden={mitText ? true : undefined}
        role={mitText ? undefined : 'img'}
        aria-label={mitText ? undefined : 'Raporto'}
        focusable="false"
      >
        {istFarbig && (
          <defs>
            <MarkenVerlauf kennung={kennung} />
          </defs>
        )}
        <path d={ZEICHEN_PFAD} fill={zeichenFuellung} />
        <path d={ZEICHEN_BEIN} fill={beinFuellung} opacity={istFarbig ? 0.92 : 1} />
      </svg>

      {mitText && (
        <span className="rp-logotext">
          <strong>Raporto</strong>
          {mitClaim && <small>Einfach mehr bewegen.</small>}
        </span>
      )}

      {unterzeile && <i className="rp-logo-zusatz">{unterzeile}</i>}
    </span>
  )
}

/**
 * Das Zeichen im Quadrat – für kompakte App- und Browserflächen.
 *
 * Auch hier bleibt das Zeichen identisch zur Wortmarke. Die Kachel gibt nur
 * einen ruhigen Rahmen, damit das farbige Zeichen auf hellen Flächen trägt.
 */
export function MarkenKachel({ groesse = 64 }: { groesse?: number }): JSX.Element {
  const reactKennung = useId()
  const kennung = `rp-markenkachel-${groesse}-${bereinigteKennung(reactKennung)}`
  const rand = 25
  const skala = (KACHEL_FELD - 2 * rand) / ZEICHEN_FELD

  return (
    <svg
      viewBox={`0 0 ${KACHEL_FELD} ${KACHEL_FELD}`}
      width={groesse}
      height={groesse}
      role="img"
      aria-label="Raporto"
      focusable="false"
    >
      <defs>
        <MarkenVerlauf kennung={kennung} />
      </defs>
      <rect width={KACHEL_FELD} height={KACHEL_FELD} rx="28" fill="#071329" />
      <g transform={`translate(${rand},${rand}) scale(${skala})`}>
        <path d={ZEICHEN_PFAD} fill={`url(#${kennung})`} />
        <path d={ZEICHEN_BEIN} fill="#ffc2d4" opacity="0.92" />
      </g>
    </svg>
  )
}
